import { refreshConsentEditor } from './magnet-consent.js';
import { refreshLegalNoticeEditor } from './magnet-legal-notice.js';
import '../styles/magnet-studio-design.css';

// Keep the original inputs and their listeners. The studio changes their location,
// never their values or the host editor's serialization contract.
export function mountStudioDesign(root) {
  const appearance = root.querySelector('section[data-tab="appearance"]');
  const source = appearance?.querySelector('.mag-modal__sec');
  if (!source) return null;
  appearance.classList.add('mag-studio-design');
  const definitions = [
    ['experience', 'Layout & invitation', 'auto_awesome'],
    ['text', 'Text & labels', 'title'],
    ['style', 'Style & colors', 'palette'],
    ['logo', 'Logo', 'image'],
    ['display', 'Display details', 'aspect_ratio'],
    ['legal', 'Terms & conditions', 'policy'],
  ];
  const overview = document.createElement('div');
  overview.className = 'mag-studio-design__overview';
  overview.innerHTML = `<h2>Design your magnet</h2><p>Choose what to adjust. See your changes on the magnet beside you.</p>
    <div class="mag-studio-design__choices">${definitions.map(([key, title, icon]) => `<button type="button" data-design-open="${key}" aria-controls="mag-design-${key}">
      <span class="material-symbols-outlined" aria-hidden="true">${icon}</span><span><strong>${title}</strong><small data-design-summary="${key}"></small></span><span aria-hidden="true">›</span></button>`).join('')}</div>`;
  const panels = Object.fromEntries(definitions.map(([key, title]) => {
    const panel = document.createElement('section');
    panel.className = 'mag-studio-design__panel';
    panel.dataset.designPanel = key; panel.id = `mag-design-${key}`; panel.hidden = true;
    panel.setAttribute('aria-labelledby', `mag-design-${key}-title`);
    panel.innerHTML = `<button type="button" class="mag-studio-design__back" data-design-back>← Back to design</button><h2 id="mag-design-${key}-title" tabindex="-1">${title}</h2>`;
    return [key, panel];
  }));

  const moveField = (selector, destination) => {
    const control = source.querySelector(selector);
    if (!control) return;
    const before = control.previousElementSibling, after = control.nextElementSibling;
    if (before?.tagName === 'LABEL') destination.append(before);
    destination.append(control);
    if (after?.classList.contains('mag-modal__hint')) destination.append(after);
  };
  const welcomeExperience = source.querySelector('#mag-welcome-experience');
  if (welcomeExperience) panels.experience.append(welcomeExperience);
  const assistantDesign = source.querySelector('#mag-assistant-design');
  if (assistantDesign) {
    if(welcomeExperience && !welcomeExperience.hidden) {const details=document.createElement('details');details.className='mag-style-customize';details.innerHTML='<summary>Conversation &amp; floating invitation</summary>';details.append(assistantDesign);panels.experience.append(details);}
    else panels.experience.append(assistantDesign);
  }
  overview.querySelector('[data-design-open="experience"]').hidden = (!assistantDesign || assistantDesign.hidden) && (!welcomeExperience || welcomeExperience.hidden);
  for (const selector of ['#mag-f-title', '#mag-f-subtitle', '#mag-f-launcher', '#mag-f-input-ph']) moveField(selector, panels.text);
  moveField('#mag-f-color', panels.style);
  const theme = source.querySelector('[name="mag-theme"]')?.closest('[role="radiogroup"]');
  if (theme) {
    const label = theme.previousElementSibling, hint = theme.nextElementSibling;
    if (label?.tagName === 'LABEL') panels.style.append(label);
    panels.style.append(theme);
    if (hint?.classList.contains('mag-modal__hint')) panels.style.append(hint);
  }
  for (const selector of ['.mag-style-gallery', '.mag-style-customize']) {
    const element = source.querySelector(selector); if (element) panels.style.append(element);
  }
  const visualDesign = source.querySelector('#mag-visual-design');
  if (visualDesign) { const details = document.createElement('details'); details.className = 'mag-style-customize'; details.innerHTML = '<summary>Shape, messages &amp; motion</summary>'; details.hidden = visualDesign.hidden; details.append(visualDesign); panels.style.append(details); }
  const styleGrid = panels.style.querySelector('.mag-style-grid');
  styleGrid?.setAttribute('aria-label', 'Appearance styles');

  const drop = source.querySelector('#mag-logo-drop');
  if (drop) {
    if (drop.previousElementSibling?.tagName === 'LABEL') drop.previousElementSibling.remove();
    panels.logo.append(drop);
    const status = source.querySelector('#mag-logo-status'); if (status) panels.logo.append(status);
  }
  const url = document.createElement('details');
  url.className = 'mag-studio-design__logo-url';
  url.innerHTML = '<summary>Use an image URL instead</summary>';
  moveField('#mag-f-logo', url); panels.logo.append(url);
  moveField('#mag-f-logo-fit', panels.logo);
  const logoSizing = source.querySelector('#mag-logo-sizing');
  if (logoSizing) panels.logo.append(logoSizing);
  const inline = source.querySelector('#mag-f-inline-size');
  if (inline) panels.display.append(inline);
  const launcher = source.querySelector('#mag-f-hide-launcher')?.closest('.mag-tog-row');
  if (launcher) {
    const hint = launcher.nextElementSibling; panels.display.append(launcher);
    if (hint?.classList.contains('mag-modal__hint')) panels.display.append(hint);
  }
  const legal = source.querySelector('#mag-legal-notice');
  if (legal) panels.legal.append(legal);
  const consent = source.querySelector('#mag-consent');
  if (consent) panels.legal.append(consent);
  source.querySelector('.mag-modal__sec-t')?.remove();
  // Preserve any future/unknown controls instead of silently dropping them.
  if (source.querySelector('input, select, textarea, button, details')) panels.display.append(source);
  else source.remove();
  appearance.append(overview, ...Object.values(panels));

  const value = selector => root.querySelector(selector)?.value || '';
  const summary = (key, text) => { overview.querySelector(`[data-design-summary="${key}"]`).textContent = text; };
  let destroyed = false;
  const refresh = () => {
    if (destroyed) return;
    const welcomeLabel = root.querySelector('[name="mag-welcome-layout"]:checked')?.closest('label')?.querySelector('strong')?.textContent;
    summary('experience', value('[name="mag-welcome-layout"]:checked') ? `${welcomeLabel} · Invitation and panel controls` : value('#mag-f-presentation') === 'assistant' ? 'Assistant welcome · Invitation and panel controls' : 'Conversation · Familiar chat layout');
    summary('text', value('#mag-f-title').trim() || 'Title, subtitle and button wording');
    const themeName = { auto: 'Automatic theme', sync: 'Matches your website', light: 'Light theme', dark: 'Dark theme' }[value('[name="mag-theme"]:checked')] || 'Theme and palette';
    const selected = panels.style.querySelector('[data-appearance-preset][aria-pressed="true"] strong')?.textContent;
    const pending = panels.style.querySelector('[data-style-pending]');
    const previewing = pending && !pending.hidden;
    const trialMode = panels.style.querySelector('[data-style-mode][aria-pressed="true"]')?.textContent.trim();
    summary('style', `${selected ? `${previewing ? 'Previewing ' : ''}${selected} · ` : ''}${previewing && trialMode ? trialMode : themeName}`);
    refreshLegalNoticeEditor(root);
    refreshConsentEditor(root);
    summary('legal', root.querySelector('#mag-legal-enabled')?.checked && (value('#mag-legal-text').trim() || panels.legal.querySelector('input[type=url]')?.value || [...panels.legal.querySelectorAll('input[type=url]')].some(el => el.value.trim())) ? 'Notice and policy links' : 'Not shown');
    if (root.querySelector('#mag-consent-terms-enabled')?.checked || root.querySelector('#mag-consent-email-enabled')?.checked) summary('legal', 'Visitor permission choices');
    summary('logo', value('#mag-f-logo').trim() ? `Logo added · ${value('#mag-f-logo-fit') === 'cover' ? 'Fills the area' : 'Whole logo visible'}` : 'Add your brand image');
    const mode = value('[name="mag-display"]:checked');
    const placement = { inline: 'Embedded on page', chat: 'Chat bubble', right_slide: 'Slide-in panel', modal: 'Pop-up' }[mode];
    summary('display', `${placement || 'Size and floating button'}${mode && mode !== 'inline' && root.querySelector('#mag-f-hide-launcher')?.checked ? ' · Button hidden' : ''}`);
  };
  const show = (key, focus = false) => {
    if (key && !panels[key]) return false;
    overview.hidden = !!key;
    for (const [name, panel] of Object.entries(panels)) panel.hidden = name !== key;
    refresh();
    if (focus && key) panels[key].querySelector('h2').focus({ preventScroll: true });
    return true;
  };
  const click = event => {
    const open = event.target.closest('[data-design-open]');
    if (open) show(open.dataset.designOpen, true);
    const back = event.target.closest('[data-design-back]');
    if (back) {
      const key = back.closest('[data-design-panel]').dataset.designPanel;
      show(null); overview.querySelector(`[data-design-open="${key}"]`).focus({ preventScroll: true });
    }
  };
  const updated = () => queueMicrotask(refresh);
  appearance.addEventListener('click', click);
  for (const event of ['input', 'change', 'click']) root.addEventListener(event, updated);
  // Style trials and asynchronous uploads can update without a field event.
  const observer = new MutationObserver(refresh);
  for (const selector of ['#mag-style-status', '#mag-logo-status']) {
    const status = root.querySelector(selector); if (status) observer.observe(status, { childList: true, subtree: true, characterData: true });
  }
  refresh();
  return {
    show,
    refresh,
    reveal(control) {
      const panel = control?.closest('[data-design-panel]');
      if (!panel || !appearance.contains(panel)) return false;
      show(panel.dataset.designPanel);
      for (let ancestor = control.parentElement; ancestor && ancestor !== appearance; ancestor = ancestor.parentElement) {
        if (ancestor.tagName === 'DETAILS') ancestor.open = true;
        if (ancestor.hidden) ancestor.hidden = false;
      }
      return true;
    },
    destroy() {
      destroyed = true; observer.disconnect(); appearance.removeEventListener('click', click);
      for (const event of ['input', 'change', 'click']) root.removeEventListener(event, updated);
    },
  };
}
